import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { InputAdornment, TextField, Box, Button, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';

export const SystemInvoiceTable = ({ poNumber, invoices = [], poDetails = null }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const filteredData = useMemo(() => {
    let data = Array.isArray(invoices) ? invoices : [];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      data = data.filter((row) =>
        Object.values(row).some((val) => val != null && String(val).toLowerCase().includes(s))
      );
    }
    return data;
  }, [invoices, searchTerm]);

  const handleOpenInvoiceDetails = useCallback(
    (rowData) => {
      const baseInvoice = rowData._originalData || rowData;
      const invoiceData = {
        ...baseInvoice,
        type: baseInvoice.type || baseInvoice.invoice_type_detail || rowData.type || null,
        invoice_type: baseInvoice.invoice_type || rowData.invoice_type || null,
        assigned_role:
          baseInvoice.assigned_role || rowData.assigned_role || rowData.assignedRole || null,
        previous_assigned_role:
          baseInvoice.previous_assigned_role || rowData.previous_assigned_role || null,
        currentApprover: rowData.currentApprover || baseInvoice.currentApprover || '',
        vendor_remarks: baseInvoice.vendor_remarks || rowData.vendor_remarks || '',
        reason_of_rejection: baseInvoice.reason_of_rejection || rowData.reason_of_rejection || '',
      };
      navigate('/systemPoWorkflowManagement/invoices/details', {
        state: {
          invoiceData,
          poNumber,
          invoices,
          poDetails,
        },
      });
    },
    [navigate, poNumber, invoices, poDetails]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice Number',
        Cell: ({ cell, row }) => (
          <Button
            variant="text"
            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
            onClick={() => handleOpenInvoiceDetails(row.original)}
          >
            {cell.getValue() || '—'}
          </Button>
        ),
      },
      { accessorKey: 'vendor', header: 'Vendor' },
      { accessorKey: 'region', header: 'Region' },
      { accessorKey: 'invoiceAmount', header: 'Amount' },
      { accessorKey: 'approvalStatus', header: 'Status' },
      { accessorKey: 'invoiceDate', header: 'Created' },
    ],
    [handleOpenInvoiceDetails]
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    state: { pagination },
    onPaginationChange: setPagination,
    enableRowActions: false,
    enablePagination: true,
    manualPagination: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleOpenInvoiceDetails(row.original),
      sx: { cursor: 'pointer' },
    }),
    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <TextField
          placeholder="Search"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ backgroundColor: '#fff', width: '300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => {
            const ws = XLSX.utils.json_to_sheet(filteredData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'system_invoices');
            XLSX.writeFile(wb, `system_invoices_${poNumber || 'po'}.xlsx`);
          }}
          sx={{ backgroundColor: '#BD1D23' }}
        >
          Download
        </Button>
      </Box>
    ),
    muiCircularProgressProps: {
      color: 'inherit',
      size: 40,
      sx: { color: '#999999' },
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
  });

  return (
    <Box sx={{ width: '100%' }}>
      <MaterialReactTable table={table} />
      {false && <CircularProgress />}
    </Box>
  );
};
